import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FolderOpen, CheckCircle, FileArchive, Download, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import CategorizedDocsSection from './CategorizedDocsSection';
import ZipUploadModal from './ZipUploadModal';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionDocumentsModal({ open, onClose, membre, token, userRole }) {
  const [showZipModal, setShowZipModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const isSuperAdmin = userRole === 'super_admin';
  const selectedCount = useMemo(() => selectedIds.size, [selectedIds]);

  const slug = (s) =>
    (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);

  const handleExportZip = async () => {
    if (!membre?.id) return;
    setExporting(true);
    const isFiltered = selectedCount > 0;
    const toastId = toast.loading(
      isFiltered
        ? `Génération du ZIP (${selectedCount} document(s) sélectionnés)…`
        : 'Génération du ZIP (tous les documents)…'
    );
    try {
      const params = isFiltered ? `?ids=${Array.from(selectedIds).join(',')}` : '';
      const res = await axios.get(
        `${API}/api/admin/membres/${membre.id}/documents/export-zip${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 180000,
        }
      );
      if (!res.data || res.data.size === 0) {
        toast.error('Aucun document à exporter pour ce membre', { id: toastId });
        return;
      }
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      const name = membre.raison_sociale || `${membre.prenom || ''}_${membre.nom || ''}`;
      const suffix = isFiltered ? `_${selectedCount}docs` : '';
      link.href = url;
      link.download = `Documents_${slug(name) || 'membre'}${suffix}_${today}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`ZIP téléchargé (${(blob.size / 1024 / 1024).toFixed(2)} Mo)`, { id: toastId });
    } catch (err) {
      const detail = err.response?.status === 404
        ? 'Aucun document trouvé pour ce membre'
        : (err.response?.data?.detail || "Échec de l'export ZIP");
      toast.error(detail, { id: toastId });
    } finally {
      setExporting(false);
    }
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="subscription-docs-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base flex-wrap">
            <FolderOpen className="h-5 w-5 text-teal-600" />
            Documents de souscription
            <Badge variant="outline" className="text-[10px] font-normal">
              {membre?.raison_sociale || `${membre?.prenom || ''} ${membre?.nom || ''}`}
            </Badge>
            <div className="ml-auto flex items-center gap-1.5">
              {isSuperAdmin && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={`h-7 px-2.5 text-xs transition-colors ${
                    selectedCount > 0
                      ? 'border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-semibold'
                      : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                  }`}
                  onClick={handleExportZip}
                  disabled={exporting}
                  data-testid="export-membre-zip-btn"
                  title={selectedCount > 0
                    ? `Exporter les ${selectedCount} document(s) sélectionnés en ZIP`
                    : "Télécharger tous les documents du membre en ZIP"}
                >
                  {exporting ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5 mr-1" />
                  )}
                  {selectedCount > 0 ? `Exporter ${selectedCount} sélectionné${selectedCount > 1 ? 's' : ''}` : 'Exporter ce membre'}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 px-2.5 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                onClick={() => setShowZipModal(true)}
                data-testid="open-zip-upload-btn"
              >
                <FileArchive className="h-3.5 w-3.5 mr-1" />
                Import ZIP
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {isSuperAdmin && selectedCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-lg text-xs">
              <span className="text-indigo-800 font-semibold">
                {selectedCount} document{selectedCount > 1 ? 's' : ''} sélectionné{selectedCount > 1 ? 's' : ''} pour l&apos;export
              </span>
              <button
                type="button"
                onClick={handleClearSelection}
                className="ml-auto inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 hover:underline"
                data-testid="clear-selection-btn"
              >
                <X className="h-3 w-3" /> Tout désélectionner
              </button>
            </div>
          )}

          <CategorizedDocsSection
            key={refreshKey}
            membre={membre}
            token={token}
            selectable={isSuperAdmin}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
          />

          <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
            <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
            <p className="text-[11px] text-emerald-700">
              Tous les documents sont automatiquement synchronisés avec la GED avec horodatage et identification de l&apos;auteur.
            </p>
          </div>
        </div>
      </DialogContent>

      <ZipUploadModal
        open={showZipModal}
        onClose={() => setShowZipModal(false)}
        membre={membre}
        token={token}
        onCommitted={() => { setRefreshKey((k) => k + 1); setShowZipModal(false); }}
      />
    </Dialog>
  );
}
